import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLiffStatus } from "@/contexts/LiffContext";
import { auth } from "@/config/firebase";
import { AuthStalledScreen } from "@/components";
import { COLORS } from "@/constants";
import { useLongPressVConsole } from "@/hooks/useLongPressVConsole";

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, stalled, error, retryProfile } = useAuth();
  const location = useLocation();
  const resolvedUser = user ?? auth.currentUser;
  const longPressVConsoleHandlers = useLongPressVConsole();
  const liffStatus = useLiffStatus();

  if (loading) {
    // The auth bootstrap has been hanging past the watchdog window — a dead
    // connection or a request that never settles. Nothing will unstick it on
    // its own, so offer a reload instead of an eternal "Loading...".
    if (stalled) {
      return (
        <AuthStalledScreen longPressHandlers={longPressVConsoleHandlers} />
      );
    }
    const liffError = liffStatus === "failed_init" ? ` ${liffStatus}` : "";
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100dvh",
          color: COLORS.text.pagePrimary,
          backgroundColor: COLORS.background.bodyDefault,
        }}
        {...longPressVConsoleHandlers}
      >
        {`Loading...${liffError}`}
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!resolvedUser) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // The session is valid but the profile fetch failed (backend down, network
  // error), and it isn't a tenant-forbidden session — that path already forces
  // a re-login inside AuthContext before `error` is ever set. Rendering pages
  // with a null profile crashes or shows blanks, so surface an in-place retry.
  if (error) {
    return (
      <AuthStalledScreen
        longPressHandlers={longPressVConsoleHandlers}
        message={error}
        onRetry={() => {
          void retryProfile();
        }}
      />
    );
  }

  // Render children if authenticated
  return <>{children}</>;
};

export default ProtectedRoute;
